// HT-3 · 効果を確認（検証セット → 検証 → 有効にする → 履歴 → 以前の学習内容に戻す）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Warning, ClockCounterClockwise, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  LEARN, authHeader, detailOf, Panel, Pill, Empty, Help, Collapse, Spinner,
} from './shared';
import ValidationSets from './ValidationSets';
import ImpactReview from './ImpactReview';
import Reread from './Reread';
import LearningDigest from './LearningDigest';

function MetricTable({ rows, testId, afterLabel }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-[11.5px]" data-testid={testId}>
        <thead>
          <tr className="text-left text-zinc-500">
            <th className="py-1 font-medium">測った内容</th>
            <th className="py-1 font-medium">これまで</th>
            <th className="py-1 font-medium">{afterLabel}</th>
            <th className="py-1 font-medium">変化</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key || r.label_ja} className="border-t border-zinc-100"
              data-testid={`${testId}-${r.key || r.label_ja}`}>
              <td className="py-1 text-zinc-700">{r.label_ja}</td>
              <td className="py-1 tabular-nums text-zinc-600">{r.before ?? '—'}</td>
              <td className="py-1 tabular-nums text-zinc-800">{r.after ?? '—'}</td>
              <td className={`py-1 ${r.better ? 'text-teal-700' : r.worse ? 'text-rose-700' : 'text-zinc-500'}`}>
                {r.change_ja}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ValidationResult({ v }) {
  const reasons = [...(v.failed_reasons_ja || []), ...(v.regression_reasons_ja || []),
    ...(v.needs_more_data_reasons_ja || [])];
  return (
    <div className="rounded-md border border-zinc-200 p-3" data-testid="validation-result">
      <p className="flex flex-wrap items-center gap-1.5">
        <Pill tone={v.status === 'passed' ? 'teal' : v.status === 'needs_more_data' ? 'amber' : 'rose'}
          testId="validation-status">{v.status_label_ja}</Pill>
        <Pill>検証用の実書類 {v.real_document_count}件</Pill>
        <Pill>誤りやすい例 {v.negative_control_count}件</Pill>
        <Pill>{v.protected_set_ja}</Pill>
        <span className="text-[11px] text-zinc-500">
          対象: バージョン {v.learning_version} · {v.validated_at?.slice(0, 16)}
        </span>
      </p>
      <p className="pt-2 text-[12px] text-zinc-800" data-testid="validation-next-step">
        次にすること: {v.recommended_next_step_ja}
      </p>
      {reasons.length > 0 && (
        <ul className="list-disc pl-5 pt-1 text-[11.5px] text-rose-800"
          data-testid="validation-reasons">
          {reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      <div className="pt-3">
        <p className="pb-1 text-[12px] font-medium text-zinc-800">検証用の実書類での比較</p>
        <MetricTable rows={v.metrics} testId="validation-metrics"
          afterLabel="この学習内容" />
      </div>
      <div className="pt-3">
        <p className="pb-1 text-[12px] font-medium text-zinc-800">守るべき読み取りでの比較</p>
        <MetricTable rows={v.protected_metrics} testId="validation-protected-metrics"
          afterLabel="この学習内容" />
        {v.protected_note_ja && (
          <p className="pt-1 text-[11px] text-zinc-500"
            data-testid="validation-protected-note">{v.protected_note_ja}</p>
        )}
      </div>
      <p className="pt-3 text-[11.5px] text-zinc-700" data-testid="validation-impact">
        検索結果に影響する書類 {v.impact?.search_documents ?? 0}件 ·
        アシスタントの回答に影響する書類 {v.impact?.assistant_documents ?? 0}件
        <span className="pl-1 text-zinc-500">{v.impact?.note_ja}</span>
      </p>
      {(v.blocked_profiles_ja || []).length > 0 && (
        <p className="pt-1 text-[11.5px] text-amber-900" data-testid="validation-blocked">
          実書類で未検証のため有効化しない種類: {v.blocked_profiles_ja.join(' / ')}
        </p>
      )}
      {(v.dual_use_violations || []).length > 0 && (
        <p className="pt-1 text-[11.5px] text-amber-900" data-testid="validation-dual-use">
          {v.dual_use_violations.map((x) => `${x.label_ja}: ${x.detail_ja}`).join(' / ')}
        </p>
      )}
      {(v.negative_control_violations || []).length > 0 && (
        <p className="pt-1 text-[11.5px] text-rose-800" data-testid="validation-negative-violations">
          {v.negative_control_violations.map((x) => x.detail_ja).join(' / ')}
        </p>
      )}
      <Collapse label="書類ごとの結果" testId="validation-documents">
        {(v.failing_documents || []).length === 0 ? (
          <p className="text-[11.5px] text-zinc-600">期待と異なる書類はありません。</p>
        ) : (
          (v.failing_documents || []).map((d) => (
            <p key={d.document_id} className="text-[11px] text-zinc-700"
              data-testid={`validation-doc-${d.document_id}`}>
              <Warning size={11} className="mr-1 inline text-rose-600" />
              {d.title || d.document_id}: {(d.issues_ja || []).join(' / ')}
            </p>
          ))
        )}
        {(v.failing_rules_ja || []).map((r) => (
          <p key={r} className="text-[11px] text-rose-700">問題のある学習内容: {r}</p>
        ))}
      </Collapse>
      <p className="pt-2 text-[11px] text-zinc-500">{v.notice_ja}</p>
    </div>
  );
}

function ActiveVersion({ a, onRollback }) {
  if (!a?.active) {
    return (
      <Panel title="有効な学習内容" testId="learn-active-version">
        <Empty testId="active-version-empty">{a?.message_ja || '有効な学習内容はありません。'}</Empty>
      </Panel>
    );
  }
  return (
    <Panel title="有効な学習内容" testId="learn-active-version"
      action={a.rollback_available ? (
        <Button size="sm" variant="outline"
          className="h-9 border-amber-300 text-[12px] text-amber-800 hover:bg-amber-50"
          onClick={onRollback} data-testid="active-version-rollback">
          <ClockCounterClockwise size={12} className="mr-1" />以前の学習内容に戻す
        </Button>
      ) : null}>
      <p className="flex flex-wrap gap-1.5">
        <Pill tone="teal" testId="active-version-number">バージョン {a.version}</Pill>
        <Pill>{a.validation_status_ja}</Pill>
        <Pill>学習内容 {a.pattern_count}件</Pill>
        <Pill>正しい例 {a.positive_example_count}件</Pill>
        <Pill>誤りやすい例 {a.negative_control_count}件</Pill>
      </p>
      <p className="pt-1.5 text-[11.5px] text-zinc-700" data-testid="active-version-meta">
        承認 {a.approved_by || '—'} · 有効化 {a.activated_at?.slice(0, 16) || '—'} ·
        対象 {(a.profiles_affected_labels_ja || []).join(' / ') || '—'} ·
        直前 {a.previous_version ? `バージョン ${a.previous_version}` : 'なし'} ·
        {a.rollback_available ? ' 以前の内容に戻せます' : ' 戻せる以前の内容はありません'}
      </p>
      {(a.blocked_profiles_ja || []).length > 0 && (
        <p className="text-[11.5px] text-amber-900" data-testid="active-version-blocked">
          実書類で未検証のため有効にしていない種類: {a.blocked_profiles_ja.join(' / ')}
        </p>
      )}
      <ul className="list-disc pl-5 pt-1.5 text-[11.5px] text-zinc-700"
        data-testid="active-version-controlled-use">
        {(a.controlled_use_ja || []).map((t) => <li key={t}>{t}</li>)}
      </ul>
      <Collapse label="渡している手がかりの内訳" testId="active-version-hints">
        {(a.hint_summary_ja || []).map((t) => (
          <p key={t} className="text-[11px] text-zinc-600">{t}</p>
        ))}
      </Collapse>
      <Collapse label="操作の記録" testId="active-version-audit">
        {(a.audit || []).map((x, i) => (
          <p key={`${x.action}-${i}`} className="text-[11px] text-zinc-600">
            {x.action} · {x.by || '—'} · {String(x.at).slice(0, 16)}
          </p>
        ))}
      </Collapse>
    </Panel>
  );
}

function RollbackDialog({ version, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      role="dialog" aria-modal="true" aria-label="以前の学習内容に戻す"
      data-testid="evaluate-rollback-dialog">
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-4">
        <h3 className="text-base font-semibold text-zinc-900">以前の学習内容に戻す</h3>
        <ul className="pt-2 space-y-1 text-[11.5px] leading-relaxed text-zinc-700">
          <li>・有効になるバージョン: {version.previous_version
            ? `バージョン ${version.previous_version}` : '既定の読み取りルール'}</li>
          <li>・今後の読み取りに、この学習内容は使われなくなります</li>
          <li>・過去の読み取り結果は変更しません</li>
          <li>・確認済みの書類の値・監査・学習候補も変更しません</li>
        </ul>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          aria-label="戻す理由" placeholder="戻す理由（必須）"
          className="mt-3 h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
          data-testid="evaluate-rollback-reason" />
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" className="h-10 text-[12px]" onClick={onCancel}
            data-testid="evaluate-rollback-cancel">やめる</Button>
          <Button className="h-10 bg-amber-700 text-[12.5px] hover:bg-amber-800"
            disabled={!reason.trim()} onClick={() => onConfirm(reason.trim())}
            data-testid="evaluate-rollback-ok">以前の学習内容に戻す</Button>
        </div>
      </div>
    </div>
  );
}

export default function Evaluate({ onChanged, onGo }) {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rollbackFor, setRollbackFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, a] = await Promise.all([
        axios.get(`${LEARN}/versions`, { headers: authHeader() }),
        axios.get(`${LEARN}/active-version`, { headers: authHeader() }),
      ]);
      setRows(v.data?.rows || []);
      setActive(a.data);
    } catch (e) { toast.error(detailOf(e, '学習履歴を取得できませんでした')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const out = await fn();
      if (ok) toast.success(typeof ok === 'function' ? ok(out) : ok);
      await load();
      onChanged?.();
      return out;
    } catch (e) {
      toast.error(detailOf(e, '処理できませんでした'));
      return null;
    } finally { setBusy(false); }
  };

  const build = () => run(async () => {
    const r = await axios.post(`${LEARN}/versions/from-approved`, {},
      { headers: authHeader() });
    setResult(null);
    return r;
  }, (r) => `承認済みの内容からバージョン ${r.data.row.version} を作りました（まだ有効ではありません）`);

  const validate = (v) => run(async () => {
    const r = await axios.post(`${LEARN}/versions/${v.id}/validate`, {},
      { headers: authHeader() });
    setResult(r.data.validation);
    return r;
  }, (r) => `検証が終わりました: ${r.data.validation.status_label_ja}`);

  const activate = (v) => run(async () => {
    const r = await axios.post(`${LEARN}/versions/${v.id}/activate`, {},
      { headers: authHeader() });
    return r;
  }, `バージョン ${v.version} を有効にしました（有効にした内容だけが手がかりとして使われます）`);

  const rollback = (reason) => run(async () => {
    const r = await axios.post(`${LEARN}/rollback`, { reason },
      { headers: authHeader() });
    setRollbackFor(null);
    setResult(null);
    return r;
  }, (r) => (r.data.restored_version
    ? `バージョン ${r.data.restored_version} に戻しました`
    : '既定の読み取りルールに戻しました'));

  const draft = rows.find((r) => r.can_evaluate && r.status !== 'rolled_back');
  const ready = rows.find((r) => r.can_activate && r.status !== 'rolled_back');

  return (
    <div className="space-y-4">
      <Panel title="効果を確認" testId="learn-evaluate"
        hint="承認済みの内容を検証用の書類で確かめ、問題がなければ有効にします。承認だけでは読み取りは変わりません。">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-10 text-[12px]" onClick={build} disabled={busy}
            data-testid="evaluate-build">
            <Plus size={13} className="mr-1" />承認済みから学習内容を作る
          </Button>
          {draft && (
            <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
              onClick={() => validate(draft)} disabled={busy} data-testid="evaluate-run">
              {busy ? <Spinner /> : null}
              <span className={busy ? 'ml-1' : ''}>バージョン {draft.version} を検証する</span>
            </Button>
          )}
          {ready && (
            <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
              onClick={() => activate(ready)} disabled={busy} data-testid="evaluate-activate">
              <Check size={13} className="mr-1" />
              バージョン {ready.version} を有効にする
            </Button>
          )}
          <Help label="検証と有効化" testId="evaluate-help">
            検証用の実書類と、守るべき読み取り、誤って適用してはいけない例で読み取りを比べます。
            悪化・不足・危険が見つかった内容は有効にできません。実書類で確認できていない種類は、
            その種類だけ有効になりません。
          </Help>
        </div>
        {!draft && !ready && !result && (
          <div className="pt-3">
            <Empty testId="evaluate-empty">
              {'検証を待っている学習内容はありません。\n「承認する」で承認した内容から作れます。'}
            </Empty>
          </div>
        )}
        {result && <div className="pt-3"><ValidationResult v={result} /></div>}
      </Panel>

      <ActiveVersion a={active}
        onRollback={() => setRollbackFor({
          version: active.version, previous_version: active.previous_version,
        })} />

      <ImpactReview />

      <Reread onChanged={load} />

      <ValidationSets onChanged={load} />

      <LearningDigest onGo={onGo} />

      <Panel title="学習履歴" testId="learn-history"
        hint="有効にした学習内容と、その検証結果です。過去の読み取り結果は書き換えません。">
        {rows.length === 0 ? (
          <Empty testId="evaluate-history-empty">学習内容の履歴はまだありません。</Empty>
        ) : (
          <div className="space-y-1.5">
            {rows.map((v) => (
              <div key={v.id} className="rounded-md border border-zinc-200 px-3 py-2"
                data-testid={`evaluate-version-${v.version}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-zinc-900">
                      バージョン {v.version}
                      <span className="ml-2"><Pill tone={v.is_active ? 'teal' : v.status === 'rolled_back' ? 'amber' : 'zinc'}>
                        {v.status_label_ja}
                      </Pill></span>
                      {(v.validation_status_ja || v.evaluated) && (
                        <span className="ml-1.5"><Pill tone={v.regression ? 'rose' : 'teal'}>
                          {v.validation_status_ja
                            || (v.regression ? '検証で問題あり' : '検証で問題なし')}
                        </Pill></span>
                      )}
                    </p>
                    <p className="pt-0.5 text-[11px] text-zinc-500">
                      学習内容 {v.pattern_count}件
                      {v.activated_at && ` · 有効化 ${String(v.activated_at).slice(0, 10)}`}
                      {v.approved_by && ` · 承認 ${v.approved_by}`}
                      {(v.profiles_affected_labels_ja || []).length
                        ? ` · 対象 ${v.profiles_affected_labels_ja.join(' / ')}` : ''}
                      {v.previous_version ? ` · 直前 v${v.previous_version}` : ''}
                      {v.rollback_reason && ` · 戻した理由: ${v.rollback_reason}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1.5">
                    {v.can_evaluate && (
                      <Button size="sm" variant="outline" className="h-9 text-[12px]"
                        disabled={busy} onClick={() => validate(v)}
                        data-testid={`evaluate-validate-${v.version}`}>検証する</Button>
                    )}
                    {v.can_activate && (
                      <Button size="sm" className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
                        disabled={busy} onClick={() => activate(v)}
                        data-testid={`evaluate-activate-${v.version}`}>有効にする</Button>
                    )}
                    {v.is_active && (
                      <Button size="sm" variant="outline"
                        className="h-9 border-amber-300 text-[12px] text-amber-800 hover:bg-amber-50"
                        onClick={() => setRollbackFor(v)}
                        data-testid={`evaluate-rollback-${v.version}`}>
                        <ClockCounterClockwise size={12} className="mr-1" />以前の学習内容に戻す
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {rollbackFor && (
        <RollbackDialog version={rollbackFor} onCancel={() => setRollbackFor(null)}
          onConfirm={rollback} />
      )}
    </div>
  );
}
